import type { HealthDeletion, HealthSample } from "../types";

const instantMetrics: Record<string, [string, string]> = {
  heart_rate_bpm: ["Heart rate", "bpm"],
  speed_mps: ["Health speed", "m/s"],
  power_w: ["Power", "W"],
  oxygen_percent: ["Oxygen", "%"],
  respiratory_rate: ["Respiration", "/min"],
};

export function resolveHealthSource(samples: HealthSample[], selected: string): string | undefined {
  const sources = [...new Set(samples.map((sample) => sample.source_package))];
  if (selected !== "preferred" && sources.includes(selected)) return selected;
  return sources.find((source) => source.toLowerCase().includes("garmin")) ?? sources[0];
}

export function healthAtTime(samples: HealthSample[], selected: string, timestamp: number): string[] {
  const source = resolveHealthSource(samples, selected);
  if (!source) return [];
  const nearest = new Map<string, HealthSample>();
  for (const sample of samples) {
    if (sample.source_package !== source || !instantMetrics[sample.metric]) continue;
    const distance = Math.abs(sample.start_time - timestamp);
    if (distance > 120) continue;
    const previous = nearest.get(sample.metric);
    if (!previous || distance < Math.abs(previous.start_time - timestamp)) nearest.set(sample.metric, sample);
  }
  return [...nearest].map(([metric, sample]) => {
    const [label, unit] = instantMetrics[metric]!;
    return `${label}: ${Math.round(sample.value * 10) / 10} ${unit}`;
  });
}

export function mergeHealth(
  current: HealthSample[],
  incoming: HealthSample[],
  deletions: HealthDeletion[] = [],
): HealthSample[] {
  const result = new Map(current.map((sample) => [healthKey(sample), sample]));
  const removed = new Set(deletions.map((item) => `${item.source_package}\0${item.record_id}`));
  for (const [key, sample] of result)
    if (removed.has(`${sample.source_package}\0${sample.record_id}`)) result.delete(key);
  for (const sample of incoming) result.set(healthKey(sample), sample);
  return [...result.values()].sort((a, b) => a.start_time - b.start_time);
}

const healthKey = (sample: HealthSample) =>
  `${sample.source_package}\0${sample.record_id}\0${sample.metric}\0${sample.start_time}`;
